import RangeSlider from 'components/RangeSlider'
import RdnPricingMethodEditor, { sameMethod } from 'components/RdnPricingMethodEditor'
import copy from 'copy.json'
import { useInputHandlers } from 'hooks/useInputHandlers'
import { LockIcon, LockOpenIcon } from 'icons'
import { MatchOffersTable } from 'pages/matches/MatchOffersTable'
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useStore } from 'store'
import styled from 'styled-components'
import {
  MatchedOffersData,
  MatchingOffers,
  MatchOptions,
  MatchTranche,
  PublicMatchingOffers,
  RdnPricingCapabilities,
  trancheKeys
} from 'types/match'
import { getDaysByTrancheKey } from 'utils/tranche-utils'
import Button from './Button'
import { PrefixedInput } from './Input'
import Spinner from './Spinner'
import Table from './Table'

type MatchPayload = MatchOptions & MatchTranche

type OfferOptionsEditorProps = {
  data: MatchingOffers | PublicMatchingOffers
  matchId: number
  value: MatchPayload
  onValueChange: Dispatch<SetStateAction<MatchPayload>>
  refetchData: () => Promise<void>
  onRdnMethodAvailabilityChange?: (available: boolean) => void
  disabled?: boolean
}

export const MatchOptionsEditor = memo(
  ({
    data,
    matchId,
    value,
    onValueChange,
    refetchData,
    onRdnMethodAvailabilityChange,
    disabled
  }: OfferOptionsEditorProps) => {
    const { api } = useStore()
    const [loading, setLoading] = useState(false)

    const [maximizerMaxSurplusPercentage, setMaximizerMaxSurplusPercentage] = useState(5)

    const handleTrancheMaximizer = async () => {
      setLoading(true)
      try {
        const newTranche = await api.maximizeTranche(matchId, {
          ...value,
          maximizer_max_surplus_percentage: maximizerMaxSurplusPercentage
        })

        if (newTranche) {
          onValueChange(
            (prev: MatchPayload): MatchPayload => ({
              ...prev,
              ...newTranche
            })
          )
        }

        await refetchData()
      } catch (error) {
        console.error('Error maximizing tranche:', error)
      } finally {
        setLoading(false)
      }
    }

    const { handleNamedInputChange, toggleBooleanValue, handleValueChange } =
      useInputHandlers<MatchPayload>(onValueChange)

    const toggleAllLocks = useCallback(
      (type: 'base' | 'peak') => () => {
        onValueChange(prev => {
          const keys = trancheKeys
            .filter(key => key.startsWith(type + '_'))
            .map(key => key + '_lock')
          const newValue = !keys.some(key => !!prev[key as keyof MatchTranche])
          return {
            ...prev,
            ...Object.fromEntries(keys.map(key => [key, newValue]))
          }
        })
      },
      [onValueChange]
    )

    const setNumberValue = handleNamedInputChange((_, value) => Number(value))
    const setEnergyPricingExtraPercent = handleValueChange('rdn_energy_pricing_extra_percent')
    const setTrancheEnergyPricingExtraPercent = handleValueChange(
      'tranche_energy_pricing_extra_percent'
    )
    const [rdnCapabilities, setRdnCapabilities] = useState<RdnPricingCapabilities | null>(null)
    const [rdnCapabilitiesError, setRdnCapabilitiesError] = useState(false)

    const loadRdnCapabilities = useCallback(async () => {
      setRdnCapabilitiesError(false)
      setRdnCapabilities(null)
      try {
        const capabilities = await api.getRdnPricingCapabilities()
        if (capabilities) setRdnCapabilities(capabilities)
        else setRdnCapabilitiesError(true)
      } catch {
        setRdnCapabilitiesError(true)
      }
    }, [api])

    useEffect(() => {
      void loadRdnCapabilities()
    }, [loadRdnCapabilities])

    const methodCapability = (method: MatchOptions['rdn_pricing_method']) =>
      rdnCapabilities?.methods.find(capability => sameMethod(capability.method, method))

    const selectedMethodCapability = methodCapability(value.rdn_pricing_method)

    useEffect(() => {
      onRdnMethodAvailabilityChange?.(
        rdnCapabilities !== null && selectedMethodCapability?.available === true
      )
    }, [onRdnMethodAvailabilityChange, rdnCapabilities, selectedMethodCapability?.available])

    const onOfferValueChange = useCallback(
      (key: keyof MatchedOffersData) => (offer_id: number, newValue: number) => {
        onValueChange(
          (prev: MatchPayload): MatchPayload => ({
            ...prev,
            custom_offers_data: prev.custom_offers_data.map(offer =>
              offer.offer_id === offer_id ? { ...offer, [key]: newValue } : offer
            )
          })
        )
      },
      [onValueChange]
    )
    const onOfferToggleLock = useCallback(
      (offer_id: number) => {
        onValueChange(
          (prev: MatchPayload): MatchPayload => ({
            ...prev,
            custom_offers_data: prev.custom_offers_data.map(offer =>
              offer.offer_id === offer_id ? { ...offer, lock: !offer.lock } : offer
            )
          })
        )
      },
      [onValueChange]
    )

    return (
      <>
        <Section>
          <SectionTitle style={{ paddingTop: 16, marginBottom: 16 }}>Kontrakty PPA</SectionTitle>

          {loading && (
            <SectionLoading>
              <Spinner size={60} centered style={{ color: 'var(--white)' }} />
            </SectionLoading>
          )}

          <SectionActions>
            <div>
              <div>
                <div style={{ textAlign: 'center' }}>Maksymalna nadwyżka</div>
                <PrefixedInput
                  name="maximizerMaxSurplusPercentage"
                  type="number"
                  value={maximizerMaxSurplusPercentage}
                  prefix="%"
                  prefixWidth="50px"
                  reverse
                  min={0}
                  max={100}
                  step={0.5}
                  style={{ width: 150, height: 30, float: 'left' }}
                  onChange={e => setMaximizerMaxSurplusPercentage(Number(e.target.value))}
                  disabled={disabled}
                />
              </div>
              <Button small secondary onClick={handleTrancheMaximizer} disabled={disabled}>
                Dopasuj kontrakty automatycznie
              </Button>
            </div>
          </SectionActions>

          <MatchOffersTable
            useMatchId={true}
            offerOptions={value}
            data={data}
            onChangeVolume={onOfferValueChange('total_power')}
            onChangePrice={onOfferValueChange('energy_price')}
            onToggleLock={onOfferToggleLock}
            disabled={disabled}
          />
        </Section>

        <Section>
          <SectionTitle>Rynek Terminowy</SectionTitle>

          {loading && (
            <SectionLoading>
              <Spinner size={60} centered style={{ color: 'var(--white)' }} />
            </SectionLoading>
          )}

          <div style={{ flex: '1 0 400px' }}>
            <Table condensed={true}>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Kontrakt</th>
                  <th>
                    <span style={{ float: 'left', marginRight: 78 }}>Pasmo</span>
                    <Button
                      secondary
                      small
                      style={{ padding: 6 }}
                      onClick={toggleAllLocks('base')}
                      disabled={disabled}
                      title="Zablokuj wartości pasm do automatycznego dopasowania kontraktów"
                    >
                      {Object.entries(value).some(
                        ([key, val]) => key.startsWith('base_') && key.endsWith('_lock') && !!val
                      ) ? (
                        <LockIcon width={16} height={16} />
                      ) : (
                        <LockOpenIcon width={16} height={16} />
                      )}
                    </Button>
                  </th>
                  <th style={{ width: 120 }}>Wolumen roczny</th>
                  <th>Cena</th>
                </tr>
              </thead>
              <tbody>
                {trancheKeys
                  .filter(key => key.startsWith('base_'))
                  .map(trancheKey => (
                    <tr key={trancheKey}>
                      <td>{trancheKey.toUpperCase()}</td>
                      <td style={{ minWidth: 160 }}>
                        <PrefixedInput
                          name={`${trancheKey}_power`}
                          type="number"
                          value={value[`${trancheKey}_power`] || 0}
                          prefix={copy.common.MWh}
                          prefixWidth="50px"
                          reverse
                          min={0}
                          step={0.5}
                          style={{ minWidth: 120, height: 30, float: 'left' }}
                          onChange={setNumberValue}
                          touched={!!value[`${trancheKey}_power`]}
                          disabled={disabled}
                        />
                        <Button
                          secondary
                          small
                          style={{ padding: 6 }}
                          onClick={toggleBooleanValue(`${trancheKey}_lock`)}
                          disabled={disabled}
                          title="Zablokuj wartość pasma do automatycznego dopasowania kontraktów"
                        >
                          {value[`${trancheKey}_lock`] ? (
                            <LockIcon width={16} height={16} />
                          ) : (
                            <LockOpenIcon width={16} height={16} />
                          )}
                        </Button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {(value[`${trancheKey}_power`] || 0) * getDaysByTrancheKey(trancheKey) * 24}{' '}
                        {copy.common.MWh}
                      </td>
                      <td>
                        <PrefixedInput
                          name={`${trancheKey}_price`}
                          type="number"
                          value={value[`${trancheKey}_price`] || 0}
                          prefix={copy.common.zl_per_mwh}
                          prefixWidth="70px"
                          min={0}
                          reverse
                          style={{ minWidth: 140, height: 30 }}
                          onChange={setNumberValue}
                          touched={!!value[`${trancheKey}_price`]}
                          invalid={!!value[`${trancheKey}_power`] && !value[`${trancheKey}_price`]}
                          disabled={disabled}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>

          <div style={{ flex: '1 0 400px' }}>
            <Table condensed={true}>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Kontrakt</th>
                  <th>
                    <span style={{ float: 'left', marginRight: 78 }}>Pasmo</span>
                    <Button
                      secondary
                      small
                      style={{ padding: 6 }}
                      onClick={toggleAllLocks('peak')}
                      disabled={disabled}
                      title="Zablokuj wartości pasm do automatycznego dopasowania kontraktów"
                    >
                      {Object.entries(value).some(
                        ([key, val]) => key.startsWith('peak_') && key.endsWith('_lock') && !!val
                      ) ? (
                        <LockIcon width={16} height={16} />
                      ) : (
                        <LockOpenIcon width={16} height={16} />
                      )}
                    </Button>
                  </th>
                  <th style={{ width: 120 }}>Wolumen roczny</th>
                  <th>Cena</th>
                </tr>
              </thead>
              <tbody>
                {trancheKeys
                  .filter(key => key.startsWith('peak_'))
                  .map(trancheKey => (
                    <tr key={trancheKey}>
                      <td>{trancheKey.toUpperCase()}</td>
                      <td style={{ minWidth: 160 }}>
                        <PrefixedInput
                          name={`${trancheKey}_power`}
                          type="number"
                          value={value[`${trancheKey}_power`] || 0}
                          prefix={copy.common.MWh}
                          prefixWidth="50px"
                          reverse
                          min={0}
                          step={0.5}
                          style={{ minWidth: 120, height: 30, float: 'left' }}
                          onChange={setNumberValue}
                          touched={!!value[`${trancheKey}_power`]}
                          disabled={disabled}
                        />
                        <Button
                          secondary
                          small
                          style={{ padding: 6 }}
                          onClick={toggleBooleanValue(`${trancheKey}_lock`)}
                          disabled={disabled}
                        >
                          {value[`${trancheKey}_lock`] ? (
                            <LockIcon width={16} height={16} />
                          ) : (
                            <LockOpenIcon width={16} height={16} />
                          )}
                        </Button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {(value[`${trancheKey}_power`] || 0) * getDaysByTrancheKey(trancheKey) * 24}{' '}
                        {copy.common.MWh}
                      </td>
                      <td>
                        <PrefixedInput
                          name={`${trancheKey}_price`}
                          type="number"
                          value={value[`${trancheKey}_price`] || 0}
                          prefix={copy.common.zl_per_mwh}
                          prefixWidth="70px"
                          reverse
                          min={0}
                          style={{ minWidth: 140, height: 30 }}
                          onChange={setNumberValue}
                          touched={!!value[`${trancheKey}_price`]}
                          invalid={!!value[`${trancheKey}_power`] && !value[`${trancheKey}_price`]}
                          disabled={disabled}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </Section>
        <Section>
          <SectionTitle>Symulacja przyszłych cen</SectionTitle>

          <RdnMethodOption>
            <OptionTitle>Metoda wyznaczania ceny energii RDN</OptionTitle>
            <RdnPricingMethodEditor
              method={value.rdn_pricing_method}
              pricingCutoffDate={value.pricing_cutoff_date}
              capabilities={rdnCapabilities}
              capabilitiesError={rdnCapabilitiesError}
              onChange={rdnPricingMethod =>
                onValueChange(previous => ({
                  ...previous,
                  rdn_pricing_method: rdnPricingMethod
                }))
              }
              onRetry={loadRdnCapabilities}
              disabled={disabled}
            />
          </RdnMethodOption>

          <Option style={{ flex: '1 0 400px' }}>
            <OptionTitle>Dodaj N% do ceny energii RDN</OptionTitle>

            <RangeSlider
              id="energyPricingExtraPercent"
              min={-100}
              max={100}
              value={value.rdn_energy_pricing_extra_percent}
              step={1}
              label={` %`}
              onChange={setEnergyPricingExtraPercent}
              displayScale
              disabled={disabled}
            />
          </Option>

          <Option style={{ flex: '1 0 400px' }}>
            <OptionTitle>Dodaj N% do ceny energii wszystkich kontraktów terminowych</OptionTitle>

            <RangeSlider
              id="trancheEnergyPricingExtraPercent"
              min={-100}
              max={100}
              value={value.tranche_energy_pricing_extra_percent}
              step={1}
              label={` %`}
              onChange={setTrancheEnergyPricingExtraPercent}
              displayScale
              disabled={disabled}
            />
          </Option>
        </Section>
        <Section>
          <SectionTitle>Koszty transakcji giełdowych</SectionTitle>

          <Option>
            <OptionTitle>Koszt świadectw pochodzenia:</OptionTitle>

            <PrefixedInput
              name={`cost_guarantee_of_source`}
              type="number"
              value={value.cost_guarantee_of_source}
              prefix={copy.common.zl_per_mwh}
              prefixWidth="70px"
              reverse
              min={0}
              style={{ width: 150 }}
              onChange={setNumberValue}
              disabled={disabled}
            />
          </Option>

          <Option>
            <OptionTitle>Wartość akcyzy:</OptionTitle>

            <PrefixedInput
              name={`cost_excise`}
              type="number"
              value={value.cost_excise}
              prefix={copy.common.zl_per_mwh}
              prefixWidth="70px"
              reverse
              min={0}
              style={{ width: 150 }}
              onChange={setNumberValue}
              disabled={disabled}
            />
          </Option>

          <Option>
            <OptionTitle>Koszt obsługi RDN:</OptionTitle>

            <PrefixedInput
              name={`cost_rdn_handling`}
              type="number"
              value={value.cost_rdn_handling}
              prefix={copy.common.zl_per_mwh}
              prefixWidth="70px"
              reverse
              min={0}
              style={{ width: 150 }}
              onChange={setNumberValue}
              disabled={disabled}
            />
          </Option>

          <Option>
            <OptionTitle>Koszt obsługi PPA:</OptionTitle>

            <PrefixedInput
              name={`cost_ppa_handling`}
              type="number"
              value={value.cost_ppa_handling}
              prefix={copy.common.zl_per_mwh}
              prefixWidth="70px"
              reverse
              min={0}
              style={{ width: 150 }}
              onChange={setNumberValue}
              disabled={disabled}
            />
          </Option>

          <Option>
            <OptionTitle>Koszt obsługi Transz:</OptionTitle>

            <PrefixedInput
              name={`cost_tranche_handling`}
              type="number"
              value={value.cost_tranche_handling}
              prefix={copy.common.zl_per_mwh}
              prefixWidth="70px"
              reverse
              min={0}
              style={{ width: 150 }}
              onChange={setNumberValue}
              disabled={disabled}
            />
          </Option>
        </Section>
        <Section>
          <SectionTitle>Inne</SectionTitle>

          <Option>
            <OptionTitle>Aktualna cena klienta:</OptionTitle>

            <PrefixedInput
              name={`custom_current_energy_price`}
              type="number"
              value={value.custom_current_energy_price || 0}
              prefix={copy.common.zl_per_mwh}
              prefixWidth="70px"
              reverse
              min={0}
              style={{ width: 150 }}
              onChange={setNumberValue}
              disabled={disabled}
            />
          </Option>
        </Section>
      </>
    )
  }
)
MatchOptionsEditor.displayName = 'MatchOptionsEditor'

const Section = styled.section`
  display: flex;
  flex-direction: row;
  align-content: stretch;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  padding-top: 16px;
  border-top: 1px #ddd dotted;
  position: relative;
`

const SectionLoading = styled.div`
  position: absolute;
  top: 0;
  left: -24px;
  right: -24px;
  bottom: -24px;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SectionActions = styled.div`
  position: absolute;
  top: 16px;
  right: 0;

  & > * {
    display: flex;
    justify-content: end;
    gap: 8px;
  }
`

const SectionTitle = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;

  padding-bottom: 16px;
  font: var(--600-16);
`

const Option = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const RdnMethodOption = styled(Option)`
  flex: 0 0 250px;
  width: 250px;
  min-width: 250px;
  max-width: 250px;
`

const OptionTitle = styled.div`
  gap: 10px;
  text-align: center;

  padding-bottom: 5px;
  font: var(--600-15);
`
